// Página principal del portafolio (ruta "/").
// Muestra una pantalla dividida: sidebar con categorías a la izquierda y un panel
// de contenido animado a la derecha. Al hacer hover sobre una categoría, el panel
// derecho cambia con un efecto de ripple (onda) que sale desde el cursor.
// En mobile, el sidebar ocupa toda la pantalla y cada categoría linkea directo a su sección.

import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import styles from './directorio.module.css';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

// Gradientes por color_key — deben coincidir con LINK_COLORS del dashboard
const GRADIENTS = {
  blue:     'linear-gradient(135deg, #0a192f 0%, #0d4f78 100%)',
  indigo:   'linear-gradient(135deg, #1a1737 0%, #2e2a7a 100%)',
  bordeaux: 'linear-gradient(135deg, #2e0404 0%, #6b1010 100%)',
  amber:    'linear-gradient(135deg, #1a1510 0%, #3d3228 100%)',
  green:    'linear-gradient(135deg, #0a1f0a 0%, #1a5c1a 100%)',
  purple:   'linear-gradient(135deg, #1a0a2e 0%, #4a1a8a 100%)',
  default:  'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
};

// Mapea la key del link al panel de contenido del desktop (solo los 4 fijos)
const KEY_TO_PANEL = {
  data:       'analisis_datos',
  diseno:     'diseno_desarrollo',
  teatro:     'teatro',
  perfumeria: 'perfumeria',
};

// Links por defecto que se muestran si la API no responde (fallback)
const FALLBACK_LINKS = [
  { id: 1, key: 'data',       label: 'Análisis de Datos',      subtitle: 'BI · SQL · Visualización',      url: '/data',      external: 0, grp: 'TÉCNICO',  color_key: 'blue',     enabled: 1 },
  { id: 2, key: 'diseno',     label: 'Diseño y Desarrollo Web', subtitle: 'React · UI/UX · Full Stack',     url: 'https://www.behance.net/cagliero', external: 1, grp: 'TÉCNICO', color_key: 'indigo', enabled: 1 },
  { id: 3, key: 'teatro',     label: 'Teatro y Ópera',          subtitle: 'Teatro Colón · Reseñas · 2026', url: '/teatro',    external: 0, grp: 'CREATIVO', color_key: 'bordeaux', enabled: 1 },
  { id: 4, key: 'perfumeria', label: 'Perfumería',              subtitle: 'Nicho · Diseñadores · Reseñas', url: '/perfumeria',external: 0, grp: 'CREATIVO', color_key: 'amber',    enabled: 1 },
];

// Datos estructurados para Google: describe a la persona y al sitio web
const SCHEMA_DATA = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Person',
      '@id': 'https://cagliero.ar/#person',
      name: 'Matias Cagliero',
      url: 'https://cagliero.ar',
      jobTitle: 'Data Analyst & Web Developer',
      sameAs: [
        'https://www.linkedin.com/in/cagliero/',
        'https://www.instagram.com/matias_cagliero',
        'https://www.tiktok.com/@matias_cagliero',
        'https://www.behance.net/cagliero',
      ],
    },
    {
      '@type': 'WebSite',
      '@id': 'https://cagliero.ar/#website',
      url: 'https://cagliero.ar',
      name: 'Matias Cagliero | Portafolio',
      author: { '@id': 'https://cagliero.ar/#person' },
    },
  ],
});

const Directorio = () => {
  const [navLinks, setNavLinks]           = useState(FALLBACK_LINKS);
  const [decos, setDecos]                 = useState({});
  // Deco custom: si empieza con "<" lo tratamos como SVG (HTML), si no como emoji (texto).
  const renderDecoValue = (v) => String(v).trim().startsWith('<')
    ? <span className={styles['pane-deco']} dangerouslySetInnerHTML={{ __html: v }} />
    : <span className={styles['pane-deco']}>{v}</span>;
  const [activeCategory, setActiveCategory] = useState('analisis_datos');
  const contentWrapperRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const rippleTimeoutRef = useRef(null);
  const navigate = useNavigate();

  // Carga los links dinámicos desde la API (con fallback si falla)
  useEffect(() => {
    fetch(`${BACKEND}/api/links`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => { if (data.links?.length) setNavLinks(data.links); if (data.decos) setDecos(data.decos); })
      .catch(() => {}); // silencioso: usa el fallback
  }, []);

  // Detecta si el usuario está en mobile para deshabilitar la animación de hover
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 992);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Activa el panel de la categoría sobre la que se hizo hover y lanza el efecto ripple.
  // El ripple arranca desde la posición del cursor dentro del panel de contenido.
  const handleCategoryHover = (category, event) => {
    if (activeCategory === category || isMobile) return;
    const contentWrapper = contentWrapperRef.current;
    if (!contentWrapper || !event.target) return;

    if (rippleTimeoutRef.current) clearTimeout(rippleTimeoutRef.current);

    const newPane = contentWrapper.querySelector(`#${category}_content`);
    const oldPane = contentWrapper.querySelector(`#${activeCategory}_content`);
    if (!newPane) return;

    // Calcula la posición del ripple relativa al panel de contenido
    const buttonRect = event.target.getBoundingClientRect();
    const wrapperRect = contentWrapper.getBoundingClientRect();
    newPane.style.setProperty('--ripple-x', `${(buttonRect.left + buttonRect.width / 2) - wrapperRect.left}px`);
    newPane.style.setProperty('--ripple-y', `${(buttonRect.top + buttonRect.height / 2) - wrapperRect.top}px`);

    setActiveCategory(category);
    newPane.classList.add(styles['is-revealing-in']);
    if (oldPane) oldPane.classList.remove(styles['is-revealing-in']);

    // Limpia la clase de animación cuando termina para que pueda reutilizarse
    rippleTimeoutRef.current = setTimeout(() => {
      newPane.classList.remove(styles['is-revealing-in']);
    }, 700);
  };

  // Navega a una ruta interna con React Router o abre una URL externa en nueva pestaña
  const handlePaneClick = (path) => {
    if (path.startsWith('http')) {
      window.open(path, '_blank', 'noopener,noreferrer');
    } else {
      navigate(path);
    }
  };

  // Permite activar un panel con teclado (Enter o Espacio) para accesibilidad
  const handleKeyDown = (e, path) => {
    if (e.key === 'Enter' || e.key === ' ') handlePaneClick(path);
  };

  return (
    <div className={styles['portfolio-container']}>
      <Helmet>
        <title>Matias Cagliero | Portafolio</title>
        <meta name="description" content="Portafolio de Matias Cagliero: análisis de datos, diseño y desarrollo web, teatro y perfumería." />
        <link rel="canonical" href="https://cagliero.ar/" />
        <meta property="og:title" content="Matias Cagliero | Portafolio" />
        <meta property="og:description" content="Portafolio de Matias Cagliero: análisis de datos, diseño y desarrollo web, teatro y perfumería." />
        <meta property="og:url" content="https://cagliero.ar/" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{SCHEMA_DATA}</script>
      </Helmet>

      <aside className={styles['sidebar']}>
        <div className={styles['sidebar-header']}>
          <h1>MATIAS CAGLIERO</h1>
          <p>Explorá mis proyectos divididos por áreas de especialización.</p>
        </div>

        <nav className={styles['nav-groups']}>
          {/* Agrupa los links por su campo `grp` y renderiza cada grupo */}
          {Object.entries(
            navLinks.reduce((acc, link) => {
              const g = link.grp || 'General';
              if (!acc[g]) acc[g] = [];
              acc[g].push(link);
              return acc;
            }, {})
          ).map(([groupName, groupLinks]) => (
            <React.Fragment key={groupName}>
              <div className={styles['group-label']}>{groupName.toUpperCase()}</div>
              <ul className={styles['category-list']}>
                {groupLinks.map(link => {
                  const panelKey = KEY_TO_PANEL[link.key];
                  const isActive = panelKey && activeCategory === panelKey;
                  return (
                    <li key={link.id} className={styles['category-item']}>
                      {link.external ? (
                        // Links externos: abrir en nueva pestaña con <a> normal
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${styles['category-button']} ${isActive ? styles['active'] : ''}`}
                          style={{ '--card-gradient': GRADIENTS[link.color_key] || GRADIENTS.default }}
                          onMouseEnter={(e) => panelKey ? handleCategoryHover(panelKey, e) : undefined}
                        >
                          <span className={styles['btn-title']}>{link.label}</span>
                          {link.subtitle && <span className={styles['btn-sub']}>{link.subtitle}</span>}
                        </a>
                      ) : (
                        // Links internos: React Router <Link> para navegación SPA sin recarga
                        <Link
                          to={link.url}
                          className={`${styles['category-button']} ${isActive ? styles['active'] : ''}`}
                          style={{ '--card-gradient': GRADIENTS[link.color_key] || GRADIENTS.default }}
                          onMouseEnter={(e) => panelKey ? handleCategoryHover(panelKey, e) : undefined}
                        >
                          <span className={styles['btn-title']}>{link.label}</span>
                          {link.subtitle && <span className={styles['btn-sub']}>{link.subtitle}</span>}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </React.Fragment>
          ))}
        </nav>
      </aside>

      <main
        className={`${styles['content-wrapper']} ${styles[`bg-${activeCategory}`]}`}
        ref={contentWrapperRef}
      >
        {/* Panel: Análisis de Datos */}
        <div
          id="analisis_datos_content"
          className={`${styles['content-area-pane']} ${activeCategory === 'analisis_datos' ? styles['active'] : ''}`}
          onClick={() => handlePaneClick('/data')}
          onKeyDown={(e) => handleKeyDown(e, '/data')}
          role="button"
          tabIndex="0"
        >
          <div className={styles['pane-inner']}>
            <h2>Análisis de Datos</h2>
            <ul className={styles['skill-list']}>
              <li>Business Intelligence &amp; SQL</li>
              <li>Visualización en Looker y Web reports (AppScript)</li>
              <li>Modelado de Datos</li>
            </ul>
            <span className={styles['explore-btn']}>Explorar Proyectos →</span>
          </div>
          {/* Red de nodos — representa grafos de datos, conexiones, BI */}
          {decos.analisis_datos ? renderDecoValue(decos.analisis_datos) : (
          <span className={styles['pane-deco']}>
            <svg width="200" height="200" viewBox="0 0 200 200" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              {/* Nodo central */}
              <circle cx="100" cy="100" r="8" strokeWidth="1.5"/>
              {/* Nodos del hexágono exterior */}
              <circle cx="100" cy="28"  r="5" strokeWidth="1.5"/>
              <circle cx="163" cy="64"  r="5" strokeWidth="1.5"/>
              <circle cx="163" cy="136" r="5" strokeWidth="1.5"/>
              <circle cx="100" cy="172" r="5" strokeWidth="1.5"/>
              <circle cx="37"  cy="136" r="5" strokeWidth="1.5"/>
              <circle cx="37"  cy="64"  r="5" strokeWidth="1.5"/>
              {/* Radios al centro */}
              <line x1="100" y1="33"  x2="100" y2="92"  strokeWidth="1"/>
              <line x1="158" y1="67"  x2="108" y2="96"  strokeWidth="1"/>
              <line x1="158" y1="133" x2="108" y2="104" strokeWidth="1"/>
              <line x1="100" y1="167" x2="100" y2="108" strokeWidth="1"/>
              <line x1="42"  y1="133" x2="92"  y2="104" strokeWidth="1"/>
              <line x1="42"  y1="67"  x2="92"  y2="96"  strokeWidth="1"/>
              {/* Anillo exterior */}
              <line x1="100" y1="28"  x2="163" y2="64"  strokeWidth="0.6"/>
              <line x1="163" y1="64"  x2="163" y2="136" strokeWidth="0.6"/>
              <line x1="163" y1="136" x2="100" y2="172" strokeWidth="0.6"/>
              <line x1="100" y1="172" x2="37"  y2="136" strokeWidth="0.6"/>
              <line x1="37"  y1="136" x2="37"  y2="64"  strokeWidth="0.6"/>
              <line x1="37"  y1="64"  x2="100" y2="28"  strokeWidth="0.6"/>
            </svg>
          </span>
          )}
        </div>

        {/* Panel: Diseño y Desarrollo */}
        <div
          id="diseno_desarrollo_content"
          className={`${styles['content-area-pane']} ${activeCategory === 'diseno_desarrollo' ? styles['active'] : ''}`}
          onClick={() => handlePaneClick('https://www.behance.net/cagliero')}
          onKeyDown={(e) => handleKeyDown(e, 'https://www.behance.net/cagliero')}
          role="button"
          tabIndex="0"
        >
          <div className={styles['pane-inner']}>
            <h2>Diseño y Desarrollo Web</h2>
            <ul className={styles['skill-list']}>
              <li>Arquitectura React &amp; AWS</li>
              <li>Diseño UI/UX</li>
              <li>Desarrollo Full Stack</li>
            </ul>
            <span className={styles['explore-btn']}>Ver Portafolio →</span>
          </div>
          {decos.diseno_desarrollo ? renderDecoValue(decos.diseno_desarrollo) : <span className={styles['pane-deco']}>⬡</span>}
        </div>

        {/* Panel: Teatro */}
        <div
          id="teatro_content"
          className={`${styles['content-area-pane']} ${activeCategory === 'teatro' ? styles['active'] : ''}`}
          onClick={() => handlePaneClick('/teatro')}
          onKeyDown={(e) => handleKeyDown(e, '/teatro')}
          role="button"
          tabIndex="0"
        >
          <div className={styles['pane-inner']}>
            <h2>Teatro y Ópera</h2>
            <ul className={styles['skill-list']}>
              <li>Temporada Teatro Colón 2026</li>
              <li>Opiniones sobre funciones</li>
              <li>Análisis de Producción</li>
            </ul>
            <span className={styles['explore-btn']}>Ver Reseñas →</span>
          </div>
          {decos.teatro ? renderDecoValue(decos.teatro) : <span className={styles['pane-deco']}>🎭</span>}
        </div>

        {/* Panel: Perfumería */}
        <div
          id="perfumeria_content"
          className={`${styles['content-area-pane']} ${activeCategory === 'perfumeria' ? styles['active'] : ''}`}
          onClick={() => handlePaneClick('/perfumeria')}
          onKeyDown={(e) => handleKeyDown(e, '/perfumeria')}
          role="button"
          tabIndex="0"
        >
          <div className={styles['pane-inner']}>
            <h2>Perfumería</h2>
            <ul className={styles['skill-list']}>
              <li>Eventos de perfumería</li>
              <li>Análisis Olfativo</li>
              <li>Estrategia de Marca</li>
            </ul>
            <span className={styles['explore-btn']}>Explorar Universo →</span>
          </div>
          {/* Frasco de perfume Art Deco — con atomizador y etiqueta */}
          {decos.perfumeria ? renderDecoValue(decos.perfumeria) : (
          <span className={styles['pane-deco']}>
            <svg width="160" height="220" viewBox="0 0 160 220" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              {/* Atomizador */}
              <line x1="80" y1="18" x2="80" y2="5"  strokeWidth="2"   strokeLinecap="round"/>
              <path d="M72 3 Q80 0 88 3"             strokeWidth="1.5" fill="none" strokeLinecap="round"/>
              {/* Tapa */}
              <rect x="61" y="18" width="38" height="17" rx="4" strokeWidth="1.5"/>
              {/* Cuello */}
              <rect x="69" y="35" width="22" height="18" strokeWidth="1.5"/>
              {/* Hombros */}
              <path d="M50 53 Q69 53 69 53 L91 53 Q110 53 110 53" strokeWidth="1.5"/>
              {/* Cuerpo del frasco */}
              <path d="M50 56 L50 188 Q50 200 63 200 L97 200 Q110 200 110 188 L110 56" strokeWidth="1.5"/>
              {/* Etiqueta */}
              <rect x="61" y="102" width="38" height="52" rx="2" strokeWidth="1"/>
              {/* Líneas de texto decorativas */}
              <line x1="68" y1="116" x2="92" y2="116" strokeWidth="0.8"/>
              <line x1="68" y1="124" x2="92" y2="124" strokeWidth="0.8"/>
              <line x1="73" y1="132" x2="87" y2="132" strokeWidth="0.8"/>
            </svg>
          </span>
          )}
        </div>
      </main>
    </div>
  );
};

export default Directorio;
